import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { pedidosService, Pedido, UpdateEstatusDto } from '../src/services/pedidosService';
import { empleadosService, Empleado } from '../src/services/empleadosService';
import { useAuth } from '../src/contexts/AuthContext';

const getFullImageUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const apiBase = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:7202/api').replace('/api', '');
  return `${apiBase}${url}`;
};

interface RepartidorViewProps {
  onClose: () => void;
}

const ESTATUS_COLORS: Record<string, string> = {
  Pendiente:  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  Preparando: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  EnCamino:   'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  Entregado:  'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  Cancelado:  'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const isDarkMode = () => {
  const theme = localStorage.getItem('theme');
  if (theme) return theme === 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const RepartidorView: React.FC<RepartidorViewProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [empleados, setEmpleados] = useState<Empleado[]>([]);
  const [selectedEmpleado, setSelectedEmpleado] = useState<Empleado | null>(null);
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [selectedPedido, setSelectedPedido] = useState<Pedido | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingPedidos, setLoadingPedidos] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dark = isDarkMode();

  useEffect(() => {
    loadEmpleados();
  }, [user?.empresaId]);

  const loadEmpleados = async () => {
    try {
      setLoading(true);
      const data = await empleadosService.getAll();
      // Filtrar por empresa del usuario logueado y especialidad Repartidor
      const repartidores = data.filter(
        (emp) =>
          emp.especialidad === 'Repartidor' &&
          (user?.empresaId ? emp.empresaId === user.empresaId : true)
      );
      setEmpleados(repartidores);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar repartidores');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEmpleado = async (emp: Empleado) => {
    setSelectedEmpleado(emp);
    setSelectedPedido(null);
    try {
      setLoadingPedidos(true);
      const data = await pedidosService.getByEmpleado(emp.empleadoId);
      const activos = data.filter(p => p.estatus !== 'Entregado' && p.estatus !== 'Cancelado');
      const entregados = data.filter(p => p.estatus === 'Entregado');
      setPedidos([...activos, ...entregados]);
    } catch (err) {
      setPedidos([]);
    } finally {
      setLoadingPedidos(false);
    }
  };

  const handleActualizarEstatus = async (pedido: Pedido, nuevoEstatus: string) => {
    const isEntregado = nuevoEstatus === 'Entregado';
    const confirmed = await Swal.fire({
      icon: isEntregado ? 'success' : 'question',
      title: isEntregado ? '¿Confirmar entrega?' : '¿Salir a entregar?',
      text: isEntregado ? 'Este pedido será marcado como entregado.' : '¿Estás seguro de que estás en camino para entregar este pedido?',
      showCancelButton: true,
      confirmButtonColor: isEntregado ? '#22c55e' : '#3b82f6',
      cancelButtonColor: '#64748b',
      confirmButtonText: isEntregado ? 'Sí, confirmar' : 'Sí, salir',
      cancelButtonText: 'Cancelar',
      background: 'rgba(15,23,42,0.9)',
      color: '#fff',
      backdrop: false,
      customClass: {
        popup: 'rounded-2xl'
      }
    });

    if (!confirmed.isConfirmed) return;

    try {
      setSaving(true);
      const dto: UpdateEstatusDto = {
        estatus: nuevoEstatus,
        descripcion: isEntregado ? 'Pedido entregado al cliente' : 'Repartidor en camino',
      };
      const updated = await pedidosService.updateEstatus(pedido.pedidoId, dto);
      setPedidos(prev => prev.map(p => p.pedidoId === updated.pedidoId ? updated : p));
      if (selectedPedido?.pedidoId === updated.pedidoId) setSelectedPedido(updated);

      if (isEntregado) {
        Swal.fire({
          icon: 'success',
          title: '¡Entrega confirmada!',
          text: `Pedido ${pedido.numeroPedido} marcado como entregado.`,
          timer: 2000,
          timerProgressBar: true,
          background: 'rgba(15,23,42,0.85)',
          color: '#fff',
          showConfirmButton: false,
          backdrop: false,
          customClass: {
            popup: 'rounded-2xl'
          }
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err instanceof Error ? err.message : 'No se pudo actualizar el estatus',
        confirmButtonColor: '#ef4444',
        background: 'rgba(15,23,42,0.85)',
        color: '#fff',
        backdrop: false,
        customClass: {
          popup: 'rounded-2xl'
        }
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });

  const activosPedidos = pedidos.filter(p => p.estatus !== 'Entregado' && p.estatus !== 'Cancelado');
  const historialPedidos = pedidos.filter(p => p.estatus === 'Entregado' || p.estatus === 'Cancelado');

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4 overflow-y-auto">
      <div className="pt-4 bg-white dark:bg-[#1a1614] w-full sm:max-w-5xl min-h-screen sm:min-h-0 sm:rounded-2xl shadow-2xl flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/10">
          <div className="flex items-center gap-3">
            {selectedEmpleado && (
              <button
                onClick={() => { setSelectedEmpleado(null); setPedidos([]); setSelectedPedido(null); }}
                className="p-1.5 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <span className="material-symbols-outlined text-gray-400">arrow_back</span>
              </button>
            )}
            <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center">
              <span className="material-symbols-outlined text-orange-500">two_wheeler</span>
            </div>
            <div>
              <h2 className="text-2xl text-secondary dark:text-white">
                {selectedEmpleado ? selectedEmpleado.nombreEmpleado : 'Vista Repartidor'}
              </h2>
              <p className="text-xs text-gray-400">
                {selectedEmpleado
                  ? `${activosPedidos.length} entregas activas`
                  : 'Selecciona tu nombre para ver tus pedidos'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
            <span className="material-symbols-outlined text-gray-400">close</span>
          </button>
        </div>

        {/* Body */}
        {!selectedEmpleado ? (
          /* Repartidor selector */
          <div className="flex-1 flex flex-col p-6">
            <p className="text-sm font-bold text-gray-500 dark:text-gray-400 mb-4">¿Quién eres?</p>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
              </div>
            ) : error ? (
              <div className="text-center py-10">
                <p className="text-red-500 font-bold">{error}</p>
                <button onClick={loadEmpleados} className="mt-3 px-4 py-2 bg-primary text-white rounded-xl text-sm font-bold">Reintentar</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {empleados.map(emp => (
                  <button
                    key={emp.empleadoId}
                    onClick={() => handleSelectEmpleado(emp)}
                    className="flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-100 dark:border-white/10 hover:border-primary hover:bg-primary/5 transition-all text-left group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center shrink-0">
                      {getFullImageUrl(emp.imagenUrl) ? (
                        <img src={getFullImageUrl(emp.imagenUrl)!} alt={emp.nombreEmpleado} className="w-12 h-12 rounded-xl object-cover" />
                      ) : (
                        <span className="text-xl font-black text-orange-400">
                          {emp.nombreEmpleado.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-black text-gray-900 dark:text-white group-hover:text-primary transition-colors truncate">
                        {emp.nombreEmpleado}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{emp.nombreNegocio}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <div className={`w-1.5 h-1.5 rounded-full ${emp.disponible ? 'bg-green-400' : 'bg-gray-300'}`}></div>
                        <span className={`text-[10px] font-bold ${emp.disponible ? 'text-green-500' : 'text-gray-400'}`}>
                          {emp.disponible ? 'Disponible' : 'No disponible'}
                        </span>
                      </div>
                    </div>
                    <span className="material-symbols-outlined text-gray-300 group-hover:text-primary transition-colors">chevron_right</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Orders view for selected repartidor */
          <div className="flex flex-1 overflow-hidden">

            {/* Orders list */}
            <div className={`flex flex-col overflow-y-auto ${selectedPedido ? 'hidden sm:flex sm:w-2/5' : 'flex-1'} border-r border-gray-100 dark:border-white/10`}>
              {loadingPedidos ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
                </div>
              ) : pedidos.length === 0 ? (
                <div className="flex flex-col items-center justify-center flex-1 py-16 text-center px-6">
                  <span className="material-symbols-outlined text-5xl text-gray-200 dark:text-white/10 mb-2">inbox</span>
                  <p className="text-gray-500 font-bold">Sin pedidos asignados</p>
                  <p className="text-xs text-gray-400 mt-1">No tienes pedidos activos en este momento</p>
                </div>
              ) : (
                <>
                  {/* Active orders */}
                  {activosPedidos.length > 0 && (
                    <>
                      <div className="px-5 py-2.5 bg-gray-50 dark:bg-white/5">
                        <p className="text-xs font-black text-gray-400 uppercase">Activos ({activosPedidos.length})</p>
                      </div>
                      {activosPedidos.map(pedido => (
                        <button
                          key={pedido.pedidoId}
                          onClick={() => setSelectedPedido(pedido)}
                          className={`text-left px-5 py-4 border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors ${
                            selectedPedido?.pedidoId === pedido.pedidoId ? 'bg-primary/5 border-l-2 border-l-primary' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-black text-gray-900 dark:text-white truncate">{pedido.numeroPedido}</p>
                              <p className="text-xs text-gray-500 truncate">{pedido.nombreCliente}</p>
                              <p className="text-xs text-gray-400 truncate">{pedido.direccionEntrega}</p>
                            </div>
                            <div className="shrink-0">
                              <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${ESTATUS_COLORS[pedido.estatus]}`}>
                                {pedido.estatus}
                              </span>
                            </div>
                          </div>
                          {/* Quick action buttons */}
                          <div className="flex gap-2 mt-2">
                            {pedido.estatus === 'Preparando' && (
                              <button
                                onClick={e => { e.stopPropagation(); handleActualizarEstatus(pedido, 'EnCamino'); }}
                                disabled={saving}
                                className="flex items-center gap-1 px-2.5 py-1 bg-orange-500 text-white rounded-lg text-[10px] font-bold hover:bg-orange-600 transition-colors disabled:opacity-50"
                              >
                                <span className="material-symbols-outlined text-sm">two_wheeler</span>
                                Salir a entregar
                              </button>
                            )}
                            {pedido.estatus === 'EnCamino' && (
                              <button
                                onClick={e => { e.stopPropagation(); handleActualizarEstatus(pedido, 'Entregado'); }}
                                disabled={saving}
                                className="flex items-center gap-1 px-2.5 py-1 bg-green-500 text-white rounded-lg text-[10px] font-bold hover:bg-green-600 transition-colors disabled:opacity-50"
                              >
                                <span className="material-symbols-outlined text-sm">check_circle</span>
                                Marcar entregado
                              </button>
                            )}
                          </div>
                        </button>
                      ))}
                    </>
                  )}

                  {/* History */}
                  {historialPedidos.length > 0 && (
                    <>
                      <div className="px-5 py-2.5 bg-gray-50 dark:bg-white/5">
                        <p className="text-xs font-black text-gray-400 uppercase">Historial ({historialPedidos.length})</p>
                      </div>
                      {historialPedidos.map(pedido => (
                        <button
                          key={pedido.pedidoId}
                          onClick={() => setSelectedPedido(pedido)}
                          className="text-left px-5 py-3 border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors opacity-60"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-black text-gray-700 dark:text-gray-300 truncate">{pedido.numeroPedido}</p>
                              <p className="text-[10px] text-gray-400">{formatDate(pedido.fechaCreacion)}</p>
                            </div>
                            <span className={`text-[10px] font-black px-2 py-0.5 rounded-full shrink-0 ${ESTATUS_COLORS[pedido.estatus]}`}>
                              {pedido.estatus}
                            </span>
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>

            {/* Order detail */}
            {selectedPedido ? (
              <div className="flex-1 flex flex-col overflow-y-auto">
                {/* Detail header */}
                <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 dark:border-white/10">
                  <button onClick={() => setSelectedPedido(null)} className="sm:hidden p-1.5 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10">
                    <span className="material-symbols-outlined text-gray-400">arrow_back</span>
                  </button>
                  <div className="flex-1">
                    <p className="text-sm font-black text-gray-900 dark:text-white">{selectedPedido.numeroPedido}</p>
                    <p className="text-xs text-gray-400">{formatDate(selectedPedido.fechaCreacion)}</p>
                  </div>
                  <span className={`text-xs font-black px-3 py-1 rounded-full ${ESTATUS_COLORS[selectedPedido.estatus]}`}>
                    {selectedPedido.estatus}
                  </span>
                </div>

                <div className="flex-1 p-5 space-y-4 overflow-y-auto">

                  {/* Main action buttons */}
                  {selectedPedido.estatus !== 'Entregado' && selectedPedido.estatus !== 'Cancelado' && (
                    <div className="flex flex-col gap-2">
                      {selectedPedido.estatus === 'Preparando' && (
                        <button
                          onClick={() => handleActualizarEstatus(selectedPedido, 'EnCamino')}
                          disabled={saving}
                          className="flex items-center justify-center gap-2 w-full py-4 bg-orange-500 text-white rounded-2xl font-black text-base hover:bg-orange-600 transition-colors disabled:opacity-50 shadow-lg shadow-orange-500/20"
                        >
                          <span className="material-symbols-outlined">two_wheeler</span>
                          Salir a entregar
                        </button>
                      )}
                      {selectedPedido.estatus === 'EnCamino' && (
                        <button
                          onClick={() => handleActualizarEstatus(selectedPedido, 'Entregado')}
                          disabled={saving}
                          className="flex items-center justify-center gap-2 w-full py-4 bg-green-500 text-white rounded-2xl font-black text-base hover:bg-green-600 transition-colors disabled:opacity-50 shadow-lg shadow-green-500/20"
                        >
                          <span className="material-symbols-outlined">check_circle</span>
                          Confirmar entrega
                        </button>
                      )}
                      {selectedPedido.estatus === 'Pendiente' && (
                        <div className="flex items-center gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-900/30 rounded-2xl">
                          <span className="material-symbols-outlined text-yellow-500">schedule</span>
                          <p className="text-sm font-bold text-yellow-700 dark:text-yellow-400">Esperando que el negocio prepare el pedido</p>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedPedido.estatus === 'Entregado' && (
                    <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-900/30 rounded-2xl">
                      <span className="material-symbols-outlined text-green-500 text-3xl">check_circle</span>
                      <div>
                        <p className="text-sm font-black text-green-700 dark:text-green-400">¡Pedido entregado!</p>
                        <p className="text-xs text-green-600 dark:text-green-500">Entrega completada exitosamente</p>
                      </div>
                    </div>
                  )}

                  {/* Client info */}
                  <div className="bg-gray-50 dark:bg-white/5 rounded-2xl p-4">
                    <p className="text-xs font-black text-gray-400 uppercase mb-3">Entregar a</p>
                    <p className="text-base font-black text-gray-900 dark:text-white">{selectedPedido.nombreCliente}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-gray-500">{selectedPedido.telefonoCliente}</p>
                      <a
                        href={`https://wa.me/52${selectedPedido.telefonoCliente?.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola ${selectedPedido.nombreCliente}, soy tu repartidor. Voy en camino con tu pedido.`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 px-2 py-1 bg-green-500 text-white rounded-lg text-xs font-bold hover:bg-green-600 transition-colors"
                      >
                        <span className="material-symbols-outlined text-sm">chat</span>
                        WhatsApp
                      </a>
                    </div>
                    <div className="flex items-start gap-2 mt-2">
                      <span className="material-symbols-outlined text-primary text-lg shrink-0 mt-0.5">location_on</span>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{selectedPedido.direccionEntrega}</p>
                    </div>
                    {selectedPedido.latitud && selectedPedido.longitud && (() => {
                      let originParam = '';
                      if (selectedPedido.urlGoogleMapsNegocio) {
                        const match = selectedPedido.urlGoogleMapsNegocio.match(/q=([-\d.]+),([-\d.]+)/);
                        if (match) originParam = `&origin=${match[1]},${match[2]}`;
                      }
                      return (
                        <a
                          href={`https://www.google.com/maps/dir/?api=1${originParam}&destination=${selectedPedido.latitud},${selectedPedido.longitud}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 mt-3 px-4 py-2 bg-blue-50 dark:bg-blue-900/10 text-blue-600 dark:text-blue-400 rounded-xl text-sm font-bold hover:bg-blue-100 transition-colors"
                        >
                          <span className="material-symbols-outlined text-base">navigation</span>
                          Abrir en Google Maps
                        </a>
                      );
                    })()}
                  </div>

                  {/* Order summary */}
                  <div className="bg-gray-50 dark:bg-white/5 rounded-2xl p-4">
                    <p className="text-xs font-black text-gray-400 uppercase mb-2">Resumen</p>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Negocio</span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">{selectedPedido.nombreNegocio}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-sm text-gray-500">Total</span>
                      <span className="text-sm font-black text-primary">${selectedPedido.total.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Seguimientos */}
                  <div>
                    <p className="text-xs font-black text-gray-400 uppercase mb-3">Historial</p>
                    <div className="space-y-2">
                      {[...selectedPedido.seguimientos].reverse().map((seg, i) => (
                        <div key={seg.seguimientoId} className="flex gap-3 items-start">
                          <div className="flex flex-col items-center">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-[10px] font-black ${i === 0 ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-white/10 text-gray-400'}`}>
                              {selectedPedido.seguimientos.length - i}
                            </div>
                            {i < selectedPedido.seguimientos.length - 1 && (
                              <div className="w-px h-4 bg-gray-200 dark:bg-white/10 mt-1"></div>
                            )}
                          </div>
                          <div className="flex-1 pb-2">
                            <p className="text-xs font-bold text-gray-900 dark:text-white">{seg.estatus}</p>
                            {seg.descripcion && <p className="text-xs text-gray-500">{seg.descripcion}</p>}
                            <p className="text-[10px] text-gray-400">{formatDate(seg.fechaCreacion)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="hidden sm:flex flex-1 items-center justify-center text-center px-6">
                <div>
                  <span className="material-symbols-outlined text-5xl text-gray-200 dark:text-white/10">two_wheeler</span>
                  <p className="text-gray-400 font-bold mt-2">Selecciona un pedido</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RepartidorView;
